import { Data } from 'plotly.js'

import {
  BaseScenarioTimeSeriesChartItemSimple,
  CanUsScenarioTimeSeriesChartItemSimple,
  SpainScenarioTimeSeriesChartItemSimple,
  SystemConfigurations
} from '@/interfaces'
import { chartMarkerColors } from '@/utils'

interface TimeSeriesChartsData {
  chartTitle: string
  yAxisTitle?: string
  chartData: Data[]
}

export const useScenarioResultsTimeSeriesChartsConfig = (
  data: BaseScenarioTimeSeriesChartItemSimple[],
  systemConfiguration?: SystemConfigurations
) => {
  if (!data || data.length === 0) return []

  if ('aFrred' in data[0]) {
    return getSpainScenarioTimeSeriesChartItemSimple(
      data as SpainScenarioTimeSeriesChartItemSimple[],
      systemConfiguration
    )
  }
  return getCanUsScenarioTimeSeriesChartItemSimple(
    data as CanUsScenarioTimeSeriesChartItemSimple[],
    systemConfiguration
  )
}

const getSpainScenarioTimeSeriesChartItemSimple = (
  data: SpainScenarioTimeSeriesChartItemSimple[],
  systemConfiguration?: SystemConfigurations
) => {
  const commonCharts = [
    {
      chartTitle: 'Energy Breakdown: DAM Energy and A/S deployment', // 1,1
      yAxisTitle: 'Power [MW-15min]',
      chartData: [
        {
          name: 'DAM Energy',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.dae),
          marker: { color: chartMarkerColors.red }
        },
        {
          name: 'aFRR Energy Up',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.aFrreu),
          marker: { color: chartMarkerColors.purple }
        },
        {
          name: 'aFRR Energy Down',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.aFrred),
          marker: { color: chartMarkerColors.darkPurple }
        },
        {
          name: 'mFRR Energy Up',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.mFrreu),
          marker: { color: chartMarkerColors.blue }
        },
        {
          name: 'mFRR Energy Down',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.mFrred),
          marker: { color: chartMarkerColors.darkBlue }
        }
      ]
    },
    {
      chartTitle:
        'Energy Price: DAM Energy and Ancillary Services Premium (Up) and Discount (Down)', // 2,1
      yAxisTitle: 'Price [EUR/MWh]',
      chartData: [
        {
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceDae),
          marker: { color: 'black' },
          showlegend: false
        },
        {
          name: 'mFRR Energy Down',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceMfrred ?? x.priceDae),
          marker: { color: chartMarkerColors.darkBlue },
          fill: 'tonexty'
        },
        {
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceDae),
          marker: { color: 'black' },
          showlegend: false
        },
        {
          name: 'mFRR Energy Up',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceMfrreu ?? x.priceDae),
          marker: { color: chartMarkerColors.blue },
          fill: 'tonexty'
        },
        {
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceDae),
          marker: { color: 'black' },
          showlegend: false
        },
        {
          name: 'aFRR Energy Down',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceAFrred ?? x.priceDae),
          marker: { color: chartMarkerColors.darkPurple },
          fill: 'tonexty'
        },
        {
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceDae),
          marker: { color: 'black' },
          showlegend: false
        },
        {
          name: 'aFRR Energy Up',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceAFrreu ?? x.priceDae),
          marker: { color: chartMarkerColors.purple },
          fill: 'tonexty'
        },
        {
          name: 'Price DAE',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceDae),
          marker: { color: chartMarkerColors.red }
        }
      ]
    },
    {
      chartTitle: 'Power Commitment: Ancillary Services (aFRR and mFRR)', // 1,2
      yAxisTitle: 'Power [MW-15min]',
      chartData: [
        {
          name: 'aFRR Power Up',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.aFrrpu),
          marker: { color: chartMarkerColors.purple }
        },
        {
          name: 'aFRR Power Down',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.aFrrpd),
          marker: { color: chartMarkerColors.darkPurple }
        },

        {
          name: 'mFRR Energy Up',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.mFrreu),
          marker: { color: chartMarkerColors.blue }
        },
        {
          name: 'mFRR Energy Down',
          type: 'bar',
          x: data.map(x => x.timestamp),
          y: data.map(x => x.mFrred),
          marker: { color: chartMarkerColors.darkBlue }
        }
      ]
    },
    {
      chartTitle: 'Power Price: aFRR Power', // 2,2
      yAxisTitle: 'Price [EUR/MWh]',
      chartData: [
        {
          name: 'aFRR Power Up',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceAFrrpu),
          marker: { color: chartMarkerColors.purple }
        },
        {
          name: 'aFRR Power Down',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.priceAFrrpd),
          marker: { color: chartMarkerColors.darkPurple }
        }
      ]
    },
    {
      chartTitle: 'BESS State-of-Energy', // 3,1
      yAxisTitle: 'Energy [MWh]',
      chartData: [
        {
          name: 'SOC',
          type: 'scatter',
          mode: 'lines',
          fill: 'tozeroy',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.lEsEnd),
          marker: { color: chartMarkerColors.darkGray }
        }
      ]
    },
    {
      chartTitle: 'Energy Ratio: aFRR Up and Down', // 3,2
      yAxisTitle: 'Ratio [%]',
      chartData: [
        {
          name: 'aFRR Up Energy Ratio',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.regUpEnToCap),
          marker: { color: chartMarkerColors.purple }
        },
        {
          name: 'aFRR Down Energy Ratio',
          type: 'scatter',
          mode: 'lines',
          x: data?.map(x => x.timestamp),
          y: data?.map(x => x.regDownEnToCap),
          marker: { color: chartMarkerColors.darkPurple }
        }
      ]
    }
  ]

  switch (systemConfiguration) {
    case SystemConfigurations.Standalone:
      return [
        ...commonCharts,
        {
          chartTitle: 'BESS Energy Breakdown', // 4,1
          yAxisTitle: 'Power [MW-15min]',
          chartData: [
            {
              name: 'DAM Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => -x.gGesDa),
              marker: { color: chartMarkerColors.red }
            },
            {
              name: 'DAM Discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gEsgDa),
              marker: { color: chartMarkerColors.darkRed }
            },
            {
              name: 'aFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrreu),
              marker: { color: chartMarkerColors.purple }
            },
            {
              name: 'aFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrred),
              marker: { color: chartMarkerColors.darkPurple }
            },
            {
              name: 'mFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrreu),
              marker: { color: chartMarkerColors.blue }
            },
            {
              name: 'mFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrred),
              marker: { color: chartMarkerColors.darkBlue }
            }
          ]
        }
      ] as TimeSeriesChartsData[]
    case SystemConfigurations.SolarPV:
      return [
        ...commonCharts,
        {
          chartTitle: 'BESS Energy Breakdown', // 4,1
          yAxisTitle: 'Power [MW-15min]',
          chartData: [
            {
              name: 'DAM Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => -x.gGesDa),
              marker: { color: chartMarkerColors.red }
            },
            {
              name: 'DAM Discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gEsgDa),
              marker: { color: chartMarkerColors.darkRed }
            },
            {
              name: 'aFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrreu),
              marker: { color: chartMarkerColors.purple }
            },
            {
              name: 'aFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrred),
              marker: { color: chartMarkerColors.darkPurple }
            },
            {
              name: 'mFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrreu),
              marker: { color: chartMarkerColors.blue }
            },
            {
              name: 'mFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrred),
              marker: { color: chartMarkerColors.darkBlue }
            },
            {
              name: 'PV Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pv),
              marker: { color: chartMarkerColors.yellow }
            },
            {
              name: 'PV Charge (from Clipped Energy)',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pvClip),
              marker: { color: chartMarkerColors.orange }
            }
          ]
        },
        {
          chartTitle: 'Solar Energy Breakdown',
          yAxisTitle: 'Power [MW-15min]',
          chartData: [
            {
              name: 'PV Grid Export',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvg),
              marker: { color: chartMarkerColors.darkOrange }
            },
            {
              name: 'BESS Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvesGrid),
              marker: { color: chartMarkerColors.green }
            },
            {
              name: 'BESS Charge (from Clipped Energy)',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvesClip),
              marker: { color: chartMarkerColors.darkGreen }
            },
            {
              name: 'PV Clipped Energy',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gpvClipDump),
              marker: { color: chartMarkerColors.yellow }
            },
            {
              name: 'Economic Curtailment',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvc),
              marker: { color: chartMarkerColors.gray }
            },
            {
              name: 'Interver Capacity',
              type: 'scatter',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pvAcCapacity),
              marker: { color: chartMarkerColors.black }
            }
          ]
        }
      ] as TimeSeriesChartsData[]
    case SystemConfigurations.SolarPVOnSharedBus:
      return [
        ...commonCharts,
        {
          chartTitle: 'BESS Energy Breakdown', // 4,1
          yAxisTitle: 'Power [MW-15min]',
          chartData: [
            {
              name: 'DAM Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => -x.gGesDa),
              marker: { color: chartMarkerColors.red }
            },
            {
              name: 'DAM Discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gEsgDa),
              marker: { color: chartMarkerColors.darkRed }
            },
            {
              name: 'aFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrreu),
              marker: { color: chartMarkerColors.purple }
            },
            {
              name: 'aFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.aFrred),
              marker: { color: chartMarkerColors.darkPurple }
            },
            {
              name: 'mFRR Energy Up',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrreu),
              marker: { color: chartMarkerColors.blue }
            },
            {
              name: 'mFRR Energy Down',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.mFrred),
              marker: { color: chartMarkerColors.darkBlue }
            },
            {
              name: 'PV Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pv),
              marker: { color: chartMarkerColors.yellow }
            }
          ]
        },
        {
          chartTitle: 'Solar Energy Breakdown',
          yAxisTitle: 'Power [MW-15min]',
          chartData: [
            {
              name: 'PV Grid Export',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvg),
              marker: { color: chartMarkerColors.darkOrange }
            },
            {
              name: 'BESS Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvesGrid),
              marker: { color: chartMarkerColors.green }
            },
            {
              name: 'Economic Curtailment',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.gPvc),
              marker: { color: chartMarkerColors.darkGreen }
            },
            {
              name: 'Interver Capacity',
              type: 'scatter',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pvAcCapacity),
              marker: { color: chartMarkerColors.black }
            }
          ]
        }
      ] as TimeSeriesChartsData[]
    default:
      return [] as TimeSeriesChartsData[]
  }
}

const getCanUsScenarioTimeSeriesChartItemSimple = (
  data: CanUsScenarioTimeSeriesChartItemSimple[],
  systemConfiguration?: SystemConfigurations
) => {
  switch (systemConfiguration) {
    case SystemConfigurations.Standalone:
      return [
        {
          chartTitle: 'Energy Breakdown',
          chartData: [
            {
              name: 'DAE',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.dae)
            },
            {
              name: 'RTE',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.rte)
            },
            {
              name: 'Net Reg Energy',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.netRegEnergy)
            },
            {
              name: 'ES Output POI',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esOutputPoi)
            },
            {
              name: 'Max Output',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.maxOutput)
            },
            {
              name: 'Min Output',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => -x.maxOutput)
            }
          ]
        },
        {
          chartTitle: 'SR and Regulation Commitment',
          chartData: [
            {
              name: 'Reg Down while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityWhileDischarging)
            },
            {
              name: 'Reg Up while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityWhileDischarging)
            },
            {
              name: 'Reg Down while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityWhileCharging)
            },
            {
              name: 'Reg Up while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityWhileCharging)
            },
            {
              name: 'SR while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinngingReserveWhileDischarging)
            },
            {
              name: 'SR while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinngingReserveWhileCharging)
            }
          ]
        },
        {
          chartTitle: 'Energy Price',
          chartData: [
            {
              name: 'Price DAE',
              type: 'scatter',
              mode: 'lines',
              x: data?.map(x => x.timestamp),
              y: data?.map(x => x.dayAheadEnergyPrice)
            },
            {
              name: 'Price RTE',
              type: 'scatter',
              mode: 'lines',
              x: data?.map(x => x.timestamp),
              y: data?.map(x => x.realTimeEnergyPrice)
            }
          ]
        },
        {
          chartTitle: 'Regulation and SR Price',
          chartData: [
            {
              name: 'Price - Reg Up Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityPrice)
            },
            {
              name: 'Price - Reg Down Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityPrice)
            },
            {
              name: 'Price - Spinning Reserve Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinningReservePrice)
            }
          ]
        },
        {
          chartTitle: 'ES Charge/Discharge Profile',
          chartData: [
            {
              name: 'ES Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esCharging)
            },
            {
              name: 'ES Discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esDischarging)
            }
            // { name: 'Wind POI', type: 'bar', ...chartData.windPoi },
            // { name: 'Wind Curtail', type: 'bar', ...chartData.windCurtail },
            // { name: 'Solar POI', type: 'bar', ...chartData.solarPoi },
            // { name: 'Solar Curtail', type: 'bar', ...chartData.solarCurtail }
          ]
        },
        {
          chartTitle: 'SOE',
          chartData: [
            {
              name: 'ES SoE end of interval',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esSoeEndOfInterval),
              fill: 'tozeroy'
            },
            {
              name: 'Max SOE',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.maxSoe)
            }
          ]
        }
      ] as TimeSeriesChartsData[]
    case SystemConfigurations.SolarPV:
    case SystemConfigurations.SolarPVOnSharedBus:
      return [
        {
          chartTitle: 'Energy Breakdown',
          chartData: [
            {
              name: 'DAE',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.dae)
            },
            {
              name: 'RTE',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.rte)
            },
            {
              name: 'Net Reg Energy',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.netRegEnergy)
            },
            {
              name: 'ES Output POI',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esOutputPoi)
            },
            {
              name: 'Max Output',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.maxOutput)
            },
            {
              name: 'Min Output',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => -x.maxOutput)
            }
          ]
        },
        {
          chartTitle: 'SR and Regulation Commitment',
          chartData: [
            {
              name: 'Reg Down while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityWhileDischarging)
            },
            {
              name: 'Reg Up while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityWhileDischarging)
            },
            {
              name: 'Reg Down while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityWhileCharging)
            },
            {
              name: 'Reg Up while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityWhileCharging)
            },
            {
              name: 'SR while discharge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinngingReserveWhileDischarging)
            },
            {
              name: 'SR while charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinngingReserveWhileCharging)
            }
          ]
        },
        {
          chartTitle: 'Energy Price',
          chartData: [
            {
              name: 'Price DAE',
              type: 'scatter',
              mode: 'lines',
              x: data?.map(x => x.timestamp),
              y: data?.map(x => x.dayAheadEnergyPrice)
            },
            {
              name: 'Price RTE',
              type: 'scatter',
              mode: 'lines',
              x: data?.map(x => x.timestamp),
              y: data?.map(x => x.realTimeEnergyPrice)
            }
          ]
        },
        {
          chartTitle: 'Regulation and SR Price',
          chartData: [
            {
              name: 'Price - Reg Up Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationUpCapacityPrice)
            },
            {
              name: 'Price - Reg Down Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.regulationDownCapacityPrice)
            },
            {
              name: 'Price - Spinning Reserve Capacity',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.spinningReservePrice)
            }
          ]
        },
        {
          chartTitle: 'Solar Energy Breakdown',
          chartData: [
            {
              name: 'ES Charge',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esCharging)
            },
            {
              name: 'Solar POI',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pvExportToGrid)
            },
            {
              name: 'Solar Curtail',
              type: 'bar',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.pvCurtailment)
            }
          ]
        },
        {
          chartTitle: 'SOE',
          chartData: [
            {
              name: 'ES SoE end of interval',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.esSoeEndOfInterval),
              fill: 'tozeroy'
            },
            {
              name: 'Max SOE',
              type: 'scatter',
              mode: 'lines',
              x: data.map(x => x.timestamp),
              y: data.map(x => x.maxSoe)
            }
          ]
        }
      ] as TimeSeriesChartsData[]
    default:
      return [] as TimeSeriesChartsData[]
  }
}
